<template>
  <div class="align-center justify-center">
    <v-card
      class="dropzone-container"
      :style="(currentDropZoneState === dropZoneState.FOCUS && 'border: 5px solid #28f569;') || (currentDropZoneState === dropZoneState.LOADED && 'border: 5px solid cyan;')  || (currentDropZoneState === dropZoneState.ERROR && 'border: 5px solid red;')"
      variant="outlined"
      @drop="dragdrop" 
      @dragover="dragover" 
      @dragleave="dragleave"      
    >
      <div class="d-flex flex-wrap flex-column ga-10">      
        <v-file-input 
          ref="inputRef"
          v-model="files"
          id="fileInput"
          hide-input
          @change="exploreFileSelection"
          :accept="`${props.contentTypes.join(', ')}`"
        ></v-file-input>

        <label for="fileInput" class="file-label">
          <div class="d-flex flex-wrap flex-column ga-5">
            <div>
              {{ dropZoneFeedback }}
            </div>
            <div class="text-center">
              <v-progress-circular 
                :model-value="loadingProgress" 
                :rotate="360" 
                :size="100" 
                :width="15" 
                :color="currentDropZoneState === dropZoneState.LOADING || currentDropZoneState === dropZoneState.LOADED ? 'pink' : ''">
                <template v-slot:default> {{ loadingProgress }} % </template>
              </v-progress-circular>
            </div>
            <div v-if="currentDropZoneState === dropZoneState.LOADED">
              <v-chip
                class="ma-5"
                color="cyan"
                label
              >
                <v-icon icon="mdi-microsoft-excel" start size="x-large"></v-icon>
                {{ filename }}
                <v-tooltip
                  activator="parent"
                  location="top"
                >{{ filenameHint }} ({{ fileSize }} bytes)</v-tooltip>
              </v-chip>
            </div> 
          </div>
        </label> 
      </div>  
    </v-card> 
  </div>
</template>

<script setup>

import { ref, watch, onMounted } from 'vue'

let files = []

const inputRef = ref(null)

const selectedFile = ref(null)
const dropZoneNofocusMessage = "click or drop file here"
const dropZoneFocusMessage = "release to drop file here"
const dropZoneFeedback = ref("")
const loadingProgress = ref(0)
const filename = ref("")
const filenameHint = ref("")
const fileSize = ref(0)

const dropZoneState = Object.freeze({
    FOCUS: Symbol('FOCUS'),
    NOFOCUS: Symbol('NOFOCUS'),
    LOADING: Symbol('LOADING'),
    LOADED: Symbol('LOADED'),
    ERROR: Symbol('ERROR')
});

const currentDropZoneState = ref(dropZoneState.NOFOCUS)

const dragover = (e) => {
  e.preventDefault()

  if (hasValidContentType(e)) {
      dropZoneFocusState()
      return
  }

  dropZoneErrorState()
}

const dragleave = (e) => {
  e.preventDefault()
  dropZoneNofocusState()
}

const dragdrop = (e) => {
  e.preventDefault() 

  if (hasValidContentType(e)) {
      dragdropFileSelection(e.dataTransfer.files)
      return
  }
  
  dropZoneNofocusState()
}

const dropZoneNofocusState = () => {
  loadingProgress.value = 0
  currentDropZoneState.value = dropZoneState.NOFOCUS
  dropZoneFeedback.value = dropZoneNofocusMessage
  selectedFile.value = null
  filename.value = ''
  files = []
  inputRef.value.value = null
}

const dropZoneFocusState = () => {
  loadingProgress.value = 0
  currentDropZoneState.value = dropZoneState.FOCUS
  dropZoneFeedback.value = dropZoneFocusMessage
  selectedFile.value = null
  filename.value = ''
}
const dropZoneLoadingState = () => {
  loadingProgress.value = 0
  currentDropZoneState.value = dropZoneState.LOADING
  dropZoneFeedback.value = `Uploading`
}

const dropZoneLoadedState = () => {
  currentDropZoneState.value = dropZoneState.LOADED
  dropZoneFeedback.value = 'Upload completed'
}

const dropZoneErrorState = () => {
  loadingProgress.value = 0
  currentDropZoneState.value = dropZoneState.ERROR
  dropZoneFeedback.value = 'Invalid file'
  selectedFile.value = null
  filename.value = ''
}

const exploreFileSelection = () => {
  dragdropFileSelection(files)
}

const hasValidContentType = (e) => {
  if (e.dataTransfer != null) {
    if (e.dataTransfer.items.length === 1) {
      if (props.contentTypes.includes(e.dataTransfer.items[0].type)) {
        return true
      }
    }
  }

  return false
}

const shortenFilename = (value, maxLength = 10) => {
  const parts = value.split('.')
  const name = parts.slice(0, -1).join('.')
  const ext = parts.pop()

  if (name.length > maxLength) {
      return `${name.slice(0, maxLength).trim()}... .${ext}`;
  }
  
  return value; 
}

const dragdropFileSelection = (e) => {
  if (e.length === 0) {
    dropZoneNofocusState()
    return
  }

  filenameHint.value = e[0].name
  filename.value = shortenFilename(filenameHint.value)
  
  dropZoneLoadingState()

  var f = e[0]

  if (f) {
      const reader = new FileReader()

      reader.onload = () => {
        selectedFile.value = reader.result // Base64 string
        dropZoneLoadedState()
      }

      reader.onprogress = (data) => {
        fileSize.value = data.total
        if (data.lengthComputable) {
          loadingProgress.value = parseInt( ((data.loaded / data.total) * 100), 10 )                                            
        }
      }

      reader.onerror = () => {
        dropZoneErrorState()
      }

      reader.readAsDataURL(f) 
  }
}

const props = defineProps({
  contentTypes: {
    type: Array,
    required: true
  }
})

const reset = () => {
  dropZoneNofocusState()
}

const emit = defineEmits(['fileToProcess'])

watch(
  selectedFile,
  (file) => { emit('fileToProcess', file) },
  { immediate: true }
)

onMounted(() => {
  dropZoneNofocusState()
})

defineExpose({
  reset
})

</script>

<style scoped>

.dropzone-container {
    padding: 10px;
    background: #f7fafc;
    border: 5px dashed #9b9b9b;
    border-radius: 10px;
    width: 250px;
    height: auto;
    min-height: 300px;
}

.file-label {
    font-size: 16px;
    display: block;
}

</style>